/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import CheckoutFooter from '@/components/footer/checkout-footer';
import MainFooter from '@/components/footer/main-footer';
import type { FooterLegal, FullFooterContent } from '@/lib/content/types';

/**
 * Two variants behind one entry point (CRCB2B-48): `full` renders navigation, social links, the
 * newsletter CTA, and the shared legal bar; `checkout` renders the wordmark and the same shared
 * legal bar only. Each variant's content promise is created once in its route's loader — never
 * composed here — so its Suspense boundary tracks a stable reference. See design.md Decision 11.
 */
type FooterProps =
    | { variant?: 'full'; footerContent: Promise<FullFooterContent> | undefined }
    | { variant: 'checkout'; legalContent: Promise<FooterLegal | null> | undefined };

export default function Footer(props: FooterProps): ReactElement {
    if (props.variant === 'checkout') {
        return <CheckoutFooter legalContent={props.legalContent} />;
    }
    return <MainFooter footerContent={props.footerContent} />;
}
